/**
 * Shared reads of the shell: folders, views, counts, settings, local settings, the plan, and the
 * account identity shown at the bottom of the sidebar. Every key comes from engine-client/keys.ts.
 */

import type { Entitlement, Folder, LocalSettings, SidebarCounts, SyncedSettings, View } from '@digester/core';
import { useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';

export function useFolders(): Folder[] {
  return useEngineQuery(qk.folders(), (e) => e.folders.list()).data ?? [];
}

export function useViews(): View[] {
  return useEngineQuery(qk.views(), (e) => e.views.list()).data ?? [];
}

export function useCounts(): SidebarCounts | undefined {
  return useEngineQuery(qk.counts(), (e) => e.messages.counts()).data;
}

export function useSettings(): SyncedSettings | undefined {
  return useEngineQuery(qk.settings(), (e) => e.settings.get()).data;
}

export function useLocalSettings(): LocalSettings | undefined {
  return useEngineQuery(qk.localSettings(), (e) => e.settings.getLocal()).data;
}

export function useEntitlement(): Entitlement | undefined {
  return useEngineQuery(qk.plan('entitlement'), (e) => e.plan.entitlement()).data;
}

export interface Identity {
  name: string;
  initials: string;
  /** "Free", "Pro", "Pro 2" … */
  plan: string;
  signedIn: boolean;
  email: string | null;
}

export function planLabel(entitlement: Entitlement | undefined): string {
  if (!entitlement || entitlement.plan === 'free') return 'Free';
  return entitlement.multiplier > 1 ? `Pro ${entitlement.multiplier}` : 'Pro';
}

export function useIdentity(): Identity {
  const settings = useSettings();
  const local = useLocalSettings();
  const entitlement = useEntitlement();
  const email = local?.account?.email ?? null;
  const name = settings?.profile.name.trim() || email || 'This device';
  const initials = (settings?.profile.name.trim() || email || 'D')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
  return { name, initials: initials || 'D', plan: planLabel(entitlement), signedIn: email !== null, email };
}
